import { Alert } from '@mui/material';
import React, { useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';

import CLocalizedText from '../../../components/CLocalizedText';
import CLocalizedTextStringStatic from '../../../components/CLocalizedTextStringStatic';
import CPageModule from '../../../components/CPageModule';
import CPageModuleSingleTable from '../../../components/CPageModuleSingleTable';
import CStatefulContainer from '../../../components/CStatefulContainer';
import SingleDateSelector from '../../../components/SingleDateSelector';
import StatisticCard from '../../../components/StatisticCard';
import Page from '../../../components/layout/Page';
import { EAppType } from '../../../enums/AppType';
import { BreakpointsInPx } from '../../../enums/BreakpointsInPx';
import { NexdosSecondaryColorEnum } from '../../../enums/NexdosSecondaryColorEnum';
import HGeneric from '../../../helpers/HGeneric';
import { updateStateAttribute, updateStateResponseStatefulAttribute, useStateExtended } from '../../../helpers/helper';
import { useAppSelector } from '../../../hooks/hooks';
import { useUsageTrackingInfo } from '../../../hooks/useUsageTrackingInfo';
import { type IResponse } from '../../../models/IResponse';
import { type PorftfolioManagementHedgeStatsDTO } from '../../../models/PortfolioManagementHedgeStatsDTO';
import { type TPandasTable } from '../../../models/TPandasTable';
import type { IUsageTrackingInfo } from '../../../models/usage-tracking/IUsageTracking';
import { SvPortfolioManagementServices } from '../../../services/SvPortfolioManagementServices';
import { SvUsageTrackingServices } from '../../../services/SvUsageTrackingServices';
import type { PortfolioManagementOutletContext } from '../PPortfolioManagementOutlet';

interface State {
  stats: IResponse<PorftfolioManagementHedgeStatsDTO>;
  summary: IResponse<{ asOfDateSec: number; summary: TPandasTable }>;
  displayWarningBanner: boolean;
  initLoadDefaultCheckComplete: boolean;
  ignoreAsOfDateChange: boolean;
}

export default function PPorfolioManagementHedge() {
  const [state, setState, getState] = useStateExtended<State>({
    stats: { state: { isLoading: true, isError: false } },
    summary: { state: { isLoading: true, isError: false } },
    displayWarningBanner: false,
    initLoadDefaultCheckComplete: false,
    ignoreAsOfDateChange: false
  });
  const usageTrackingParams: IUsageTrackingInfo = useUsageTrackingInfo(EAppType.PortfolioManagementHedge);

  const { locale, dashboardOutletDimensions } = useAppSelector((state) => state.dashboard);
  const context = useOutletContext<PortfolioManagementOutletContext>();
  const { factsheetId } = context;
  const factsheetID = factsheetId ?? '';
  const heading = CLocalizedTextStringStatic('portfolioManagement.nav.hedge');
  const dateLabel = CLocalizedTextStringStatic('globalSingleDatelabel');
  const cacheLabel = CLocalizedTextStringStatic('globalCacheUpdatedAt');
  const titleStr = CLocalizedTextStringStatic('ops.hedge.title');
  const gammaStr = CLocalizedTextStringStatic('ops.hedge.gamma');
  const vegaStr = CLocalizedTextStringStatic('ops.hedge.vega');

  useEffect(() => {
    (async () => {
      const currentState = await getState();

      if (currentState.ignoreAsOfDateChange && currentState.initLoadDefaultCheckComplete) {
        updateStateAttribute<State>({ ignoreAsOfDateChange: false }, setState);
        return;
      }

      await Promise.all([
        updateStateResponseStatefulAttribute<State, typeof state.stats.data>(
          { stats: currentState.stats },
          setState,
          async () =>
            await SvPortfolioManagementServices.getHedgeStatistics({
              factsheetID,
              date: Math.round((context.asOfDateTsMs ?? 0) / 1000)
            })
        ),
        updateStateResponseStatefulAttribute<State, typeof state.summary.data>(
          { summary: currentState.summary },
          setState,
          async () =>
            await SvPortfolioManagementServices.getHedgeSummary({
              factsheetID,
              date: Math.round((context.asOfDateTsMs ?? 0) / 1000)
            })
        )
      ]);

      if (!state.initLoadDefaultCheckComplete && !context.isAsOfExplicit) {
        const nextState = await getState();
        const asOfSec = nextState.summary.data?.asOfDateSec ?? 0;
        if (asOfSec > 0) {
          const backendMs = asOfSec * 1000;
          updateStateAttribute<State>({ ignoreAsOfDateChange: true }, setState);
          context.handleAsOfDateTsMsChange(
            backendMs,
            !HGeneric.isSameDayInLocalTimezone([new Date(backendMs), new Date(context.asOfDateTsMs)])
          );
        }
      }

      updateStateAttribute<State>({ initLoadDefaultCheckComplete: true }, setState);
    })();
  }, [context.asOfDateTsMs, context.isAsOfExplicit, locale,factsheetID]);

  useEffect(() => {
    updateStateAttribute<State>(
      {
        displayWarningBanner:
          !HGeneric.isSameDayInLocalTimezone([
            new Date((state.stats.data?.asOfDateSec ?? 0) * 1000),
            new Date(context.asOfDateTsMs ?? 0)
          ]) &&
          !state.stats.state.isLoading &&
          !state.stats.state.isError &&
          !state.summary.state.isLoading &&
          !state.summary.state.isError &&
          !!state.stats.data &&
          !!state.summary.data
      },
      setState
    );
  }, [state.stats, state.summary, context.asOfDateTsMs]);

  useEffect(() => {
    (async () => {
      await SvUsageTrackingServices.createUsageTrackingRecord(usageTrackingParams);
    })();
  }, []);

  return (
    <Page title={heading}>
      <CPageModule>
        <h3>{titleStr}</h3>
        <CLocalizedText dictKey={'ops.hedge.desc'} />
        <SingleDateSelector
          label={dateLabel}
          minDate={context.hedgeMinDate}
          maxDate={context.nowTimestamp}
          initDate={context.asOfDateTsMs ?? context.nowTimestamp}
          limitWidth
          shouldDisableDate={context.shouldDisableHedgeAsOfDate}
          onChange={(val) => {
            context.handleAsOfDateTsMsChange(val);
          }}
        />
        {state.displayWarningBanner && (
          <Alert severity={'warning'}>
            <CLocalizedText dictKey={'ops.hedge.warning'} />{' '}
          </Alert>
        )}
      </CPageModule>
      <CPageModule fullWidth>
        <CPageModule submodule>
          <CStatefulContainer apiRequestState={state.stats.state} showContent={!!state.stats.data}>
            <div className={'flex flex-col gap-2'}>
              <div
                className={'grid gap-4'}
                style={{
                  gridTemplateColumns: `repeat(${dashboardOutletDimensions.w >= BreakpointsInPx.BREAKPOINT_2 ? 2 : 1}, minmax(0, 1fr))`
                }}
              >
                <StatisticCard
                  label={gammaStr}
                  value={state.stats.data?.gamma}
                  backgroundColor={NexdosSecondaryColorEnum.TEAL_PPT_6}
                />
                <StatisticCard
                  label={vegaStr}
                  value={state.stats.data?.vega}
                  backgroundColor={NexdosSecondaryColorEnum.TEAL_PPT_2}
                />
              </div>
              <p className={'text-xs text-right mr-2'}>
                {cacheLabel}
                {HGeneric.formatString((state.stats.data?.asOfDateSec ?? 0) * 1000, {
                  in: 'tzTimestamp',
                  out: 'newYorkLocalTimestampExtended'
                })}
              </p>
            </div>
          </CStatefulContainer>
        </CPageModule>
        {(!!state.stats.data || !!state.summary.data) && (
          <CStatefulContainer apiRequestState={state.summary.state} showContent={!!state.summary.data} submodule>
            <CPageModuleSingleTable
              data={state.summary.data?.summary}
              cacheMeta={{
                cacheTimestamp: (state.summary.data?.asOfDateSec ?? 0) * 1000,
                timeToLive: 60 * 15,
                out: 'newYorkLocalTimestampExtended'
              }}
              colAlignmentArr={['left', 'left', 'right', 'right', 'right', 'right', 'right', 'right']}
              colSpanArr={[0.7, 1.4, 0.8, 1, 0.8, 1.2, 1.2, 1.2]}
            />
          </CStatefulContainer>
        )}
      </CPageModule>
    </Page>
  );
}
